import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { serversApi } from '@/lib/servers-api';
import { cn } from '@/lib/utils';
import type { Server } from '@/types/api';

/** How long "trusted" stays on screen. A failure stays until it is dismissed. */
const TRUSTED_MESSAGE_MS = 10_000;

/**
 * "Trust new host key", for a server whose last check met a different SSH host key.
 *
 * ## Behind a confirmation that says when it is safe
 *
 * A changed key is what a rebuild looks like, and also what an intercepted connection looks like.
 * Only the customer knows which, so the dialog asks the one question that decides it: did you
 * rebuild or reinstall this server yourself? Trusting the key otherwise could hand the platform's
 * credentials to whatever machine is answering in its place.
 *
 * ## The outcome is said here
 *
 * The platform forgets the old key and connects again at once, so beside the button it says either
 * that the new key is recorded, or why the connection then failed and that the next successful test
 * records the key. Once trusted the button goes, and the message stays in its place until read.
 */
export function TrustHostKey({
  server,
  onTrusted,
  className,
}: {
  server: Server;
  /** Told when the key was trusted, so a page can drop what it said about the failed check. */
  onTrusted?: () => void;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const { permitsChanges } = useSubscriptionAccess();
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState<{ trusted: boolean; text: string } | null>(null);

  const trust = useMutation({
    mutationFn: () => serversApi.trustNewHostKey(server.id),
    onSuccess: (updated) => {
      setConfirming(false);
      setOutcome(
        updated.status === 'online' || updated.status === 'unsupported_os'
          ? { trusted: true, text: 'New host key trusted. The server is connected and its new key is recorded.' }
          : {
              // The reason is the server's error, shown just above; this says what it means for the key.
              trusted: false,
              text: 'The old key was cleared, but connecting again failed, so the new key is not recorded yet. The next successful test records it.',
            },
      );
      onTrusted?.();
      void queryClient.invalidateQueries({ queryKey: ['servers'] });
      void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
      // The server page's status pill reads the metrics response, which otherwise waits five minutes.
      void queryClient.invalidateQueries({ queryKey: ['metrics', 'server', server.id] });
    },
  });

  useEffect(() => {
    if (outcome?.trusted !== true) return;
    const timer = window.setTimeout(() => setOutcome(null), TRUSTED_MESSAGE_MS);

    return () => window.clearTimeout(timer);
  }, [outcome]);

  if (!server.host_key_changed && outcome === null) return null;

  return (
    <div className={cn('space-y-2', className)} aria-live="polite">
      {server.host_key_changed && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            trust.reset();
            setOutcome(null);
            setConfirming(true);
          }}
          disabled={!permitsChanges || trust.isPending}
          title={permitsChanges ? undefined : 'Changing a server needs an active subscription.'}
        >
          <KeyRound aria-hidden />
          Trust new host key
        </Button>
      )}

      {outcome !== null && (
        <p
          className={cn(
            'inline-flex max-w-full items-start gap-1.5 rounded-md py-1 pl-2 pr-1 text-xs',
            outcome.trusted ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
          )}
        >
          {outcome.trusted ? (
            <CheckCircle2 className="mt-px size-3.5 shrink-0" aria-hidden />
          ) : (
            <XCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          )}
          <span className="min-w-0">{outcome.text}</span>
          <button
            type="button"
            onClick={() => setOutcome(null)}
            aria-label="Dismiss"
            className="-my-0.5 shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="size-3" aria-hidden />
          </button>
        </p>
      )}

      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open) setConfirming(false);
        }}
        title={`Trust the new host key for ${server.name}?`}
        description={`${server.endpoint} now presents a different SSH host key. That is expected if you rebuilt or reinstalled this server yourself. If you did not, someone may be intercepting the connection: do not trust it, and ask your hosting provider what changed. Trusting it clears the recorded key and connects again, recording the key the server presents now.`}
        confirmLabel="Trust new host key"
        busy={trust.isPending}
        error={trust.error}
        onConfirm={() => trust.mutate()}
      />
    </div>
  );
}
